import { Filter, Search, X } from "lucide-react";
import { useEffect, useState, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { UsersPageFilters } from "@/features/users/hooks/useUsersPage";

interface UsersBottomSheetProps {
  filters: UsersPageFilters;
  onFiltersChange: (filters: Partial<UsersPageFilters>) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  isLoading?: boolean;
}

/**
 * Bottom Sheet component for mobile user filters.
 * Opens from the bottom of the screen with a slide-up animation.
 * Contains search + role filter, matching the same filters as UsersFilters.
 */
export function UsersBottomSheet({
  filters,
  onFiltersChange,
  searchTerm,
  onSearchChange,
  isLoading = false,
}: UsersBottomSheetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue(searchTerm);
  }, [searchTerm]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearchChange(inputValue);
    }
  };

  const handleRoleChange = (value: string) => {
    onFiltersChange({ role: value });
  };

  const handleApply = () => {
    onSearchChange(inputValue);
    setIsOpen(false);
  };

  // Count active filters for badge
  const activeFilterCount =
    (searchTerm ? 1 : 0) + (filters.role && filters.role !== "Todos los roles" ? 1 : 0);

  return (
    <>
      {/* Trigger button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="relative tap-target"
      >
        <Filter className="h-4 w-4 mr-2" />
        Filtros
        {activeFilterCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
            {activeFilterCount}
          </span>
        )}
      </Button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Bottom Sheet */}
      {isOpen && (
        <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-strong border-t border-gray-200 dark:border-gray-700">
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Filtrar usuarios
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 tap-target"
                aria-label="Cerrar filtros"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 pb-4 space-y-4">
              {/* Search */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Buscar
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Nombre, email..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="pl-10 text-base h-11"
                  />
                </div>
              </div>

              {/* Sort By filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Ordenar por
                </label>
                <Select
                  value={filters.sortBy || "active_imaging"}
                  onValueChange={(val) =>
                    onFiltersChange({ sortBy: val as "active_imaging" | "created_at" })
                  }
                  disabled={isLoading}
                >
                  <SelectTrigger className="h-11 text-base">
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active_imaging">
                      Con Imagenología Activa
                    </SelectItem>
                    <SelectItem value="created_at">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Role filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
                  Rol
                </label>
                <Select
                  value={filters.role}
                  onValueChange={handleRoleChange}
                  disabled={isLoading}
                >
                  <SelectTrigger className="h-11 text-base">
                    <SelectValue placeholder="Todos los roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Todos los roles">Todos los roles</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                    <SelectItem value="Médico">Médico</SelectItem>
                    <SelectItem value="Paciente">Paciente</SelectItem>
                    <SelectItem value="Institución">Institución</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Apply button */}
              <Button
                onClick={handleApply}
                className="w-full min-h-[48px] text-base touch-action-manipulation"
                disabled={isLoading}
              >
                Aplicar filtros
              </Button>
            </div>

            {/* Safe area padding */}
            <div className="safe-bottom" />
          </div>
        </div>
      )}
    </>
  );
}
